// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { formatUsDateLocal, matchesDob } from '../../api/format'
import { FileText, Download, Trash2, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { DEFAULT_PAGE_SIZE, preferredPageSize, rememberPageSize } from '../../components/Pager'
import SearchableSelect from '../../components/common/SearchableSelect'
import TypedDeleteConfirm from '../../components/common/TypedDeleteConfirm'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

function fmtBytes(n: number): string {
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${n} B`
}
function fmtDate(iso: string | null): string {
  return formatUsDateLocal(iso)
}

// D-UI-2: a funnel on every column; each value is what the cell shows (dates stay raw).
const DOC_FUNNELS: FunnelColumn<api.MyPharmacyDocument>[] = [
  { key: 'title', label: 'Document', value: d => d.title || d.document_type },
  { key: 'patient_name', label: 'Patient', value: d => d.patient_name },
  { key: 'prescriber_name', label: 'Prescriber', value: d => d.prescriber_name },
  { key: 'document_type', label: 'Type', value: d => d.document_type },
  { key: 'size_bytes', label: 'Size', value: d => fmtBytes(d.size_bytes) },
  { key: 'received_at', label: 'Received', value: d => d.received_at },
  { key: 'expiration_date', label: 'Expires', value: d => d.expiration_date },
  { key: 'uploaded_by', label: 'Uploaded by', value: d => (d as any).uploaded_by },
]

/**
 * Settings → Documents. Pharmacy-wide view of patient attachments transferred through OwlRx,
 * as a sortable / filterable / paged table. OwlRx is transfer staging, not the system of
 * record — download anything you need into your own archive; delete what you no longer keep here.
 */
export default function DocumentsTab() {
  const [list, setList] = useState<api.MyPharmacyDocumentList | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'received_at', dir: -1 })
  const [pageSize, setPageSize] = useState(() => preferredPageSize())
  const [page, setPage] = useState(1)
  const [pendingDelete, setPendingDelete] = useState<api.MyPharmacyDocument | null>(null)

  async function load() {
    setLoading(true); setErr(null)
    try { setList(await api.getMyPharmacyDocuments()) }
    catch (e) { setErr(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  async function download(d: api.MyPharmacyDocument) {
    setBusyId(d.id); setErr(null)
    try { const r = await api.downloadPatientDocument(d.patient_id, d.id); window.open(r.url, '_blank', 'noopener') }
    catch (e) { setErr(friendlyError(e)) }
    finally { setBusyId(null) }
  }
  // BA-27: typed-confirm delete — a stray double-Enter can never destroy a document.
  async function remove(d: api.MyPharmacyDocument) { setPendingDelete(d) }
  async function reallyRemove() {
    const d = pendingDelete; if (!d) return
    setBusyId(d.id); setErr(null)
    try { await api.deleteMyPharmacyDocument(d.id); setPendingDelete(null); await load() }
    catch (e) { setErr(friendlyError(e)) }
    finally { setBusyId(null) }
  }

  const types = useMemo(() => {
    const s = new Set<string>()
    for (const d of list?.documents ?? []) if (d.document_type) s.add(d.document_type)
    return Array.from(s).sort()
  }, [list])

  const term = q.trim().toLowerCase()
  const filtered = useMemo(() => (list?.documents ?? [])
    .filter(d => !typeFilter || d.document_type === typeFilter)
    .filter(d => !term ||
      (d.title || '').toLowerCase().includes(term) ||
      (d.patient_name || '').toLowerCase().includes(term) ||
      (d.prescriber_name || '').toLowerCase().includes(term) ||
      (d.document_type || '').toLowerCase().includes(term) ||
      (((d as any).uploaded_by || '') as string).toLowerCase().includes(term) ||
      matchesDob(q, (d as any).patient_dob))
    .sort((a, b) => {
      const k = sort.key
      const num = k === 'size_bytes'
      const va = num ? (a.size_bytes ?? 0) : String((a as any)[k] ?? '').toLowerCase()
      const vb = num ? (b.size_bytes ?? 0) : String((b as any)[k] ?? '').toLowerCase()
      return va < vb ? -sort.dir : va > vb ? sort.dir : 0
    }), [list, term, q, typeFilter, sort])

  // D-UI-2: the column funnels run on the whole searched set, and the result is what gets paged.
  const fx = useTableFunnels(filtered, DOC_FUNNELS)
  const pages = Math.max(1, Math.ceil(fx.rows.length / pageSize))
  const cur = Math.min(page, pages)
  const shown = fx.rows.slice((cur - 1) * pageSize, cur * pageSize)
  useEffect(() => { setPage(1) }, [term, typeFilter, pageSize, fx.sig])

  const toggleSort = (key: string) =>
    setSort(s => s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 })
  const sTh = (key: string, label: string) => (
    <th key={key} scope="col" onClick={() => toggleSort(key)}
        aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
        style={{ ...th, cursor: 'pointer', userSelect: 'none' }}>
      {label}{sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''} {fx.funnel(key)}
    </th>
  )

  return (
    <div style={{ maxWidth: 'var(--page-max, 1400px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={18} /> Documents</h2>
        <button onClick={load} disabled={loading} style={iconBtn} title="Refresh" aria-label="Refresh">
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
        </button>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--color-text-soft)', lineHeight: 1.5, maxWidth: '100%' }}>
        Patient attachments transferred through OwlRx. This isn't your system of record — download anything you need into
        your own archive, and delete what you no longer need to keep here.
      </p>

      {err && <div style={errBox}><AlertCircle size={14} /> {err}</div>}
      {loading && !list && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>}

      {list && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by document, patient, DOB, doctor, or uploader…"
                   aria-label="Search documents"
                   style={{ flex: 1, minWidth: 240, maxWidth: 380, padding: '7px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 8 }} />
            <SearchableSelect value={typeFilter} onChange={setTypeFilter} ariaLabel="Filter by document type"
              placeholder="All types" style={{ minWidth: 170 }}
              options={[{ value: '', label: 'All types' }, ...types.map(v => ({ value: v, label: v }))]} />
            <SearchableSelect value={String(pageSize)} onChange={v => { const n = Number(v) || DEFAULT_PAGE_SIZE; rememberPageSize(n); setPageSize(n) }} ariaLabel="Rows per page"
              placeholder="Per page" style={{ minWidth: 120 }}
              options={[10, 20, 50, 100].map(n => ({ value: String(n), label: `${n} per page` }))} />
            <span style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>{fx.rows.length} of {list.documents.length}</span>
          </div>
          {filtered.length === 0
            ? <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No documents{term || typeFilter ? ' match your filters' : ''}.</div>
            : (
              <>
                {fx.chips}
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'visible' }}>
                  <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {sTh('title', 'Document')}
                        {sTh('patient_name', 'Patient')}
                        {sTh('prescriber_name', 'Prescriber')}
                        {sTh('document_type', 'Type')}
                        {sTh('size_bytes', 'Size')}
                        {sTh('received_at', 'Received')}
                        {sTh('expiration_date', 'Expires')}
                        {sTh('uploaded_by', 'Uploaded by')}
                        <th style={th} aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map(d => (
                        <tr key={d.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                          <td style={td}>
                            <div style={{ fontWeight: 600, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {d.title || d.document_type}
                              {d.is_expired && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: '#dc2626' }}>EXPIRED</span>}
                            </div>
                          </td>
                          <td style={td}>{d.patient_name || '—'}</td>
                          <td style={td}>{d.prescriber_name || '—'}</td>
                          <td style={td}>{d.document_type || '—'}</td>
                          <td style={td}>{fmtBytes(d.size_bytes)}</td>
                          <td style={td}>{fmtDate(d.received_at)}</td>
                          <td style={td}>{d.expiration_date ? fmtDate(d.expiration_date) : '—'}</td>
                          <td style={td}>{(d as any).uploaded_by ?? '—'}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            {d.has_file && (
                              <button onClick={() => download(d)} disabled={busyId === d.id} style={iconBtn} title="Download" aria-label="Download">
                                {busyId === d.id ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
                              </button>
                            )}
                            {list.can_delete && (
                              <button onClick={() => remove(d)} disabled={busyId === d.id}
                                      style={{ ...iconBtn, marginLeft: 6, color: '#dc2626', borderColor: '#fecaca' }} title="Delete" aria-label="Delete document">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, fontSize: 12.5 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={cur <= 1} style={pgBtn}>Prev</button>
                  <span style={{ color: 'var(--color-text-mute)' }}>Page {cur} / {pages}</span>
                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={cur >= pages} style={pgBtn}>Next</button>
                </div>
              </>
            )}
        </>
      )}
      <TypedDeleteConfirm
        open={!!pendingDelete}
        title="Delete document permanently?"
        message={`You are deleting "${pendingDelete?.title || pendingDelete?.document_type || 'this document'}"${pendingDelete?.patient_name ? ` for ${pendingDelete.patient_name}` : ''}. OwlRx isn't your system of record — download it first if you need a copy.`}
        busy={!!pendingDelete && busyId === pendingDelete.id}
        onConfirm={reallyRemove}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--color-text-mute)', fontWeight: 600, whiteSpace: 'nowrap', background: 'var(--color-surface-2, transparent)' }
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top' }
const pgBtn: React.CSSProperties = { padding: '5px 12px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', fontSize: 12.5 }
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 7, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', flexShrink: 0 }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', fontSize: 13, marginBottom: 14 }
